import { Grid, Typography } from '@mui/material';
import UPButton from 'components/button';
import UPInput from 'components/input';
import PasswordsMatchText from 'components/password-strength/PasswordsMatchText';
import PasswordStrength from 'components/password-strength/PasswordStrength';
import { OnboardingContactStepResponse } from 'constants/OnboardingEnums';
import { OnboardingFlowContext } from 'modules/onboarding/OnboardingFlow';
import { AccountStepFieldNames, OnboardingFlowAccountStepFields } from 'modules/onboarding/types';
import React, { useContext, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { sendOnboardingAccountStepAction } from 'store/actions/company-onboarding-actions';
import theme from 'theme';
import {
  EMPLOYEES__SAME_EMAIL_AS_EMPLOYEE_ERROR,
  GENERAL__USERNAME,
  LOGIN__PASSWORD_LABEL,
  ONBOARDING__COMPLETE_ONBOARDING,
  ONBOARDING__PREVIOUS_STEP,
  SET_PASSWORD__CONFIRM_PASSWORD_LABEL,
} from 'translations/constants';
import { getInputErrorText, validatePassword } from 'utils/helpers';
import {
  OnboardingFlowCard,
  OnboardingFlowCardActionsContainer,
  OnboardingFlowCardContentContainer,
  OnboardingFlowCardContentRow,
  OnboardingFlowStepContainer,
} from './styles';

const FormDefaultValues: OnboardingFlowAccountStepFields = {
  password: '',
  confirmPassword: '',
};

const AccountStep: React.FC = () => {
  const {
    t,
    activeStep,
    onClickPreviousStep,
    onboardingData,
    setOnboardingData,
    companyIdNumber,
    languageId,
    isExistingManager,
  } = useContext(OnboardingFlowContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingFlowAccountStepFields>({
    defaultValues: onboardingData?.account || FormDefaultValues,
  });

  const passwordWatch = watch(AccountStepFieldNames.PASSWORD);
  const confirmPasswordWatch: string = watch(AccountStepFieldNames.CONFIRM_PASSWORD);

  const onProceed: SubmitHandler<OnboardingFlowAccountStepFields> = async data => {
    try {
      setLoading(true);
      setError(false);

      const dataToSend = {
        ...data,
        languageId,
      };

      await dispatch(sendOnboardingAccountStepAction({ data: dataToSend, companyIdNumber })).unwrap();

      setOnboardingData(prev => ({
        ...prev,
        account: data,
      }));

      navigate('/login');
    } catch (err: any) {
      console.error('Error in AccountStep:', err);
      setError(true);
      setErrorMessage(err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const canSubmit = () => {
    if (isExistingManager) {
      return true;
    }

    return passwordWatch === confirmPasswordWatch && validatePassword(passwordWatch);
  };

  const onSubmit = () => {
    if (!canSubmit()) {
      return;
    }
    handleSubmit(onProceed, err => console.log(err))();
  };

  return (
    <OnboardingFlowStepContainer activeStep={activeStep}>
      <OnboardingFlowCard>
        <OnboardingFlowCardContentContainer>
          <OnboardingFlowCardContentRow container fullWidth>
            <Grid item xs={6} padding={theme.spacing(1)}>
              <UPInput
                label={t(GENERAL__USERNAME)}
                value={onboardingData?.contact?.email}
                placeholder={t(GENERAL__USERNAME)}
                onChange={null}
                disabled
              />
            </Grid>
          </OnboardingFlowCardContentRow>
          <OnboardingFlowCardContentRow container fullWidth>
            <Grid item xs={6} padding={theme.spacing(1)}>
              <Controller
                name={AccountStepFieldNames.PASSWORD}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(LOGIN__PASSWORD_LABEL)}
                    value={field.value}
                    placeholder={t(LOGIN__PASSWORD_LABEL)}
                    onChange={event => {
                      field.onChange(event.target.value);
                      setError(false);
                    }}
                    required
                    error={!!errors.password}
                    type={isExistingManager ? 'password' : 'text'}
                    helperText={isExistingManager ? getInputErrorText(errors, AccountStepFieldNames.PASSWORD) : null}
                  />
                )}
              />
              {!isExistingManager && <PasswordStrength password={passwordWatch} />}
            </Grid>
            {!isExistingManager && (
              <Grid item xs={6} padding={theme.spacing(1)}>
                <Controller
                  name={AccountStepFieldNames.CONFIRM_PASSWORD}
                  control={control}
                  rules={{ required: !isExistingManager }}
                  render={({ field }) => (
                    <UPInput
                      label={t(SET_PASSWORD__CONFIRM_PASSWORD_LABEL)}
                      value={field.value}
                      placeholder={t(SET_PASSWORD__CONFIRM_PASSWORD_LABEL)}
                      onChange={field.onChange}
                      required
                      error={!!errors.confirmPassword}
                      helperText={getInputErrorText(errors, AccountStepFieldNames.CONFIRM_PASSWORD)}
                    />
                  )}
                />
                {confirmPasswordWatch.length > 0 && (
                  <PasswordsMatchText passwordsMatch={passwordWatch === confirmPasswordWatch} />
                )}
              </Grid>
            )}
          </OnboardingFlowCardContentRow>
        </OnboardingFlowCardContentContainer>
        {error && (
          <Typography variant="body1" color="error" ml={theme.spacing(3)}>
            {t(errorMessage)}
          </Typography>
        )}
        <OnboardingFlowCardActionsContainer>
          <UPButton text={t(ONBOARDING__PREVIOUS_STEP)} onClick={onClickPreviousStep} outlined />
          <UPButton
            text={t(ONBOARDING__COMPLETE_ONBOARDING)}
            onClick={onSubmit}
            disabled={loading || (!validatePassword(passwordWatch) && !isExistingManager)}
          />
        </OnboardingFlowCardActionsContainer>
      </OnboardingFlowCard>
    </OnboardingFlowStepContainer>
  );
};

export default AccountStep;
